import { useCallback, useState } from 'react';
import { SingleSubmissionStep } from '../components/SingleProgramSubmission/types';
import { EventingLocation } from '../types/enums';
import useFlowTrackingEvents from './useFlowTrackingEvents';
import { useDynamicExperienceContext } from '../providers/DynamicExperienceProvider';

type UseSingleSubmissionSlideout = ({
  productClickedCorrelationId,
}: {
  productClickedCorrelationId: string;
}) => {
  showSlideoutExperience: boolean;
  closeSlideoutExperience: () => void;
  resetSlideoutExperience: () => void;
  dismissSlideoutExperience: () => void;
  openSlideoutExperience: () => void;
  singleSubmissionExperienceStep: SingleSubmissionStep;
  goToSlideoutPiiConfirmation: () => void;
  goToSlideoutExitStrategy: () => void;
  goToSlideoutAdditionalQuestions: () => void;
};

const useSingleSubmissionSlideout: UseSingleSubmissionSlideout = ({
  productClickedCorrelationId,
}) => {
  const [step, setStep] = useState<SingleSubmissionStep>(
    SingleSubmissionStep.PII_CONFIRMATION,
  );
  const [showSlideoutExperience, setShowSlideoutExperience] =
    useState<boolean>(false);

  const { eventContext } = useDynamicExperienceContext();
  const { triggerElementClickedEvent } = useFlowTrackingEvents();

  // Silent state reset shared by both the user-close path and the
  // error-degrade path. Resetting without an analytics event keeps the
  // contained drawer-render failure (`LeadFlowDrawerBoundary.onError`) from
  // being recorded as a user-initiated close.
  const resetSlideoutExperience = useCallback(() => {
    setShowSlideoutExperience(false);
    setStep(SingleSubmissionStep.PII_CONFIRMATION);
  }, []);

  // Hide the drawer without a close click and without returning to the
  // contact form. The body stays mounted through the exit animation, so
  // either of those would remount PiiConfirmation and re-fire FormViewed
  // / FieldViewed. The next open sets the entry step.
  const dismissSlideoutExperience = useCallback(() => {
    setShowSlideoutExperience(false);
  }, []);

  const closeSlideoutExperience = useCallback(() => {
    // Close the drawer WITHOUT resetting `step`. The drawer body stays
    // mounted through vaul's exit animation, so flipping `step` back to
    // PII_CONFIRMATION here would swap a non-PII view (e.g. EXIT_STRATEGY)
    // for PiiConfirmation mid-close, remounting PiiFormSection and
    // re-firing its conversion-drawer `FormViewed` / `FieldViewed` view
    // events while the user is closing the drawer. The next open always
    // re-asserts the entry step (`openGovernedFlow` calls
    // `goToSlideout{PiiConfirmation,ExitStrategy}`), so the reset is
    // redundant on this path.
    setShowSlideoutExperience(false);
    triggerElementClickedEvent({
      ...eventContext,
      productClickedCorrelationId,
      elementType: 'dexp-slideout-close',
      name: 'dexp-slideout-close-button',
      text: 'X',
      location: EventingLocation.CONVERSION_DRAWER,
    });
  }, [productClickedCorrelationId, eventContext, triggerElementClickedEvent]);

  const openSlideoutExperience = useCallback(
    () => setShowSlideoutExperience(true),
    [],
  );

  const goToSlideoutPiiConfirmation = useCallback(
    () => setStep(SingleSubmissionStep.PII_CONFIRMATION),
    [],
  );

  const goToSlideoutAdditionalQuestions = useCallback(
    () => setStep(SingleSubmissionStep.ADDITIONAL_QUESTIONS),
    [],
  );
  const goToSlideoutExitStrategy = useCallback(
    () => setStep(SingleSubmissionStep.EXIT_STRATEGY),
    [],
  );

  return {
    showSlideoutExperience,
    closeSlideoutExperience,
    resetSlideoutExperience,
    dismissSlideoutExperience,
    openSlideoutExperience,
    singleSubmissionExperienceStep: step,
    setSingleSubmissionExperienceStep: setStep,
    goToSlideoutPiiConfirmation,
    goToSlideoutExitStrategy,
    goToSlideoutAdditionalQuestions,
  };
};

export default useSingleSubmissionSlideout;
